/**
 * Procedural Web Audio Sound Engine for VoxelCraft.
 *
 * Generates all sound effects entirely in real-time via Web Audio API oscillators,
 * noise buffers, resonant biquad filters, and envelopes. Zero external audio files.
 */

import {
  AIR,
  BEDROCK,
  BRICKS,
  CHEST,
  COAL_ORE,
  COBBLESTONE,
  CRAFTING_TABLE,
  DIAMOND_ORE,
  DIRT,
  FURNACE,
  GLASS,
  GOLD_BLOCK,
  GOLD_ORE,
  GRASS,
  GRAVEL,
  IRON_ORE,
  LEAVES,
  LOG,
  PLANKS,
  SAND,
  SANDSTONE,
  SNOW,
  STONE,
  TORCH,
  WATER,
  WHITE_WOOL,
} from "../world/blocks";

export type BlockMaterialSound =
  | "grass"
  | "dirt"
  | "stone"
  | "wood"
  | "glass"
  | "wool"
  | "sand"
  | "snow";

/** Map block ID to material sound profile. */
export function getBlockSoundCategory(blockId: number): BlockMaterialSound {
  switch (blockId) {
    case GRASS:
    case LEAVES:
      return "grass";
    case DIRT:
    case GRAVEL:
      return "dirt";
    case SAND:
    case SANDSTONE:
      return "sand";
    case SNOW:
      return "snow";
    case LOG:
    case PLANKS:
    case CRAFTING_TABLE:
    case CHEST:
    case TORCH:
      return "wood";
    case GLASS:
      return "glass";
    case WHITE_WOOL:
      return "wool";
    case STONE:
    case COBBLESTONE:
    case BEDROCK:
    case COAL_ORE:
    case IRON_ORE:
    case GOLD_ORE:
    case DIAMOND_ORE:
    case BRICKS:
    case FURNACE:
    case GOLD_BLOCK:
    default:
      return "stone";
  }
}

export class SoundEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private whiteNoiseBuffer: AudioBuffer | null = null;
  private pinkNoiseBuffer: AudioBuffer | null = null;

  private _muted = false;
  private _volume = 0.5;
  private initialized = false;

  constructor() {
    try {
      if (typeof localStorage !== "undefined") {
        const savedMuted = localStorage.getItem("voxelcraft:sound_muted");
        if (savedMuted !== null) this._muted = savedMuted === "true";
        const savedVol = localStorage.getItem("voxelcraft:sound_volume");
        if (savedVol !== null) {
          const v = parseFloat(savedVol);
          if (Number.isFinite(v)) this._volume = Math.max(0, Math.min(1, v));
        }
      }
    } catch {
      // Ignore storage access errors
    }
  }

  get muted(): boolean {
    return this._muted;
  }

  get isMuted(): boolean {
    return this._muted;
  }

  set muted(m: boolean) {
    this._muted = m;
    try {
      localStorage.setItem("voxelcraft:sound_muted", String(m));
    } catch {}
    this.updateMasterVolume();
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    return this.muted;
  }

  get volume(): number {
    return this._volume;
  }

  set volume(v: number) {
    this._volume = Math.max(0, Math.min(1, v));
    try {
      localStorage.setItem("voxelcraft:sound_volume", String(this._volume));
    } catch {}
    this.updateMasterVolume();
  }

  /** Initialize AudioContext on first user interaction to comply with autoplay policy. */
  init(): void {
    if (this.initialized) {
      if (this.ctx && this.ctx.state === "suspended") {
        this.ctx.resume().catch(() => {});
      }
      return;
    }

    const AudioContextClass =
      (typeof window !== "undefined" &&
        (window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)) ||
      (typeof globalThis !== "undefined" &&
        (globalThis as unknown as { AudioContext?: typeof AudioContext }).AudioContext);
    if (!AudioContextClass) return;

    try {
      this.ctx = new AudioContextClass();
      this.masterGain = this.ctx.createGain();
      this.updateMasterVolume();
      this.masterGain.connect(this.ctx.destination);

      // Generate 2 seconds of white and pink noise buffers
      this.createNoiseBuffers();
      this.initialized = true;

      if (this.ctx.state === "suspended" && typeof window !== "undefined") {
        const resume = () => {
          this.ctx?.resume().catch(() => {});
          window.removeEventListener("click", resume);
          window.removeEventListener("keydown", resume);
          window.removeEventListener("pointerdown", resume);
        };
        window.addEventListener("click", resume, { once: true });
        window.addEventListener("keydown", resume, { once: true });
        window.addEventListener("pointerdown", resume, { once: true });
      }
    } catch (e) {
      console.warn("Failed to initialize SoundEngine AudioContext:", e);
    }
  }

  private updateMasterVolume(): void {
    if (!this.masterGain || !this.ctx) return;
    const target = this._muted ? 0 : this._volume;
    this.masterGain.gain.setValueAtTime(target, this.ctx.currentTime);
  }

  private createNoiseBuffers(): void {
    if (!this.ctx) return;
    const sampleRate = this.ctx.sampleRate;
    const length = sampleRate * 2; // 2 seconds buffer

    // 1. White noise
    this.whiteNoiseBuffer = this.ctx.createBuffer(1, length, sampleRate);
    const whiteData = this.whiteNoiseBuffer.getChannelData(0);
    for (let i = 0; i < length; i++) {
      whiteData[i] = Math.random() * 2 - 1;
    }

    // 2. Pink noise (Paul Kellet's filter method)
    this.pinkNoiseBuffer = this.ctx.createBuffer(1, length, sampleRate);
    const pinkData = this.pinkNoiseBuffer.getChannelData(0);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      pinkData[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
  }

  private getDestination(gainMultiplier = 1.0, pan = 0): AudioNode | null {
    if (!this.ctx || !this.masterGain || this._muted || this.ctx.state !== "running") {
      if (this.ctx && this.ctx.state === "suspended") {
        this.ctx.resume().catch(() => {});
      }
      return null;
    }
    const gain = this.ctx.createGain();
    gain.gain.value = gainMultiplier;

    if (this.ctx.createStereoPanner && Math.abs(pan) > 0.01) {
      const panner = this.ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, pan));
      gain.connect(panner);
      panner.connect(this.masterGain);
    } else {
      gain.connect(this.masterGain);
    }
    return gain;
  }

  // =========================================================================
  // MATERIAL SOUNDS: DIG, BREAK, PLACE, STEP
  // =========================================================================

  /**
   * Sound played on each footstep while walking or running.
   */
  playFootstep(blockId: number, isSprinting = false): void {
    if (blockId === AIR) return;
    const dest = this.getDestination(isSprinting ? 0.35 : 0.22);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const mat = getBlockSoundCategory(blockId);
    const rate = 0.88 + Math.random() * 0.25;

    this.synthesizeMaterialSound(mat, dest, t, rate, 0.06, 0.45);
  }

  /**
   * Sound played repeatedly while holding left click to mine a block.
   */
  playDig(blockId: number): void {
    if (blockId === AIR || blockId === WATER) return;
    const dest = this.getDestination(0.32);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const mat = getBlockSoundCategory(blockId);
    const rate = 0.85 + Math.random() * 0.3;

    this.synthesizeMaterialSound(mat, dest, t, rate, 0.08, 0.6);
  }

  /**
   * Sound played when a block finishes breaking into particles.
   */
  playBlockBreak(blockId: number): void {
    if (blockId === AIR || blockId === WATER) return;
    const dest = this.getDestination(0.65);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const mat = getBlockSoundCategory(blockId);

    // 3 rapid micro-bursts to simulate shattering debris
    for (let i = 0; i < 3; i++) {
      const offset = i * 0.024;
      const rate = 0.75 + Math.random() * 0.45;
      this.synthesizeMaterialSound(mat, dest, t + offset, rate, 0.12, 0.7);
    }
  }

  /**
   * Sound played when placing a block in the world.
   */
  playBlockPlace(blockId: number): void {
    if (blockId === AIR || blockId === WATER) return;
    const dest = this.getDestination(0.55);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const mat = getBlockSoundCategory(blockId);
    const rate = 0.95 + Math.random() * 0.15;

    this.synthesizeMaterialSound(mat, dest, t, rate, 0.11, 0.85);
  }

  private synthesizeMaterialSound(
    mat: BlockMaterialSound,
    dest: AudioNode,
    time: number,
    pitchRate: number,
    duration: number,
    intensity: number,
  ): void {
    if (!this.ctx || !this.whiteNoiseBuffer || !this.pinkNoiseBuffer) return;

    switch (mat) {
      case "grass": {
        // Soft bandpass pink noise with crisp top rustle
        const src = this.ctx.createBufferSource();
        src.buffer = this.pinkNoiseBuffer;
        src.playbackRate.value = pitchRate * 1.1;

        const filter = this.ctx.createBiquadFilter();
        filter.type = "bandpass";
        filter.frequency.setValueAtTime(1100 * pitchRate, time);
        filter.Q.value = 1.8;

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.001, time);
        gain.gain.linearRampToValueAtTime(0.7 * intensity, time + 0.005);
        gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

        src.connect(filter);
        filter.connect(gain);
        gain.connect(dest);
        src.start(time);
        src.stop(time + duration);
        break;
      }

      case "wood": {
        // Hollow bandpass noise + low triangle punch (warm wooden thud)
        const src = this.ctx.createBufferSource();
        src.buffer = this.whiteNoiseBuffer;
        src.playbackRate.value = pitchRate;

        const filter = this.ctx.createBiquadFilter();
        filter.type = "bandpass";
        filter.frequency.setValueAtTime(420 * pitchRate, time);
        filter.Q.value = 3.2;

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.001, time);
        gain.gain.linearRampToValueAtTime(0.85 * intensity, time + 0.004);
        gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

        src.connect(filter);
        filter.connect(gain);
        gain.connect(dest);
        src.start(time);
        src.stop(time + duration);

        // Low resonant thump
        const osc = this.ctx.createOscillator();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(120 * pitchRate, time);
        osc.frequency.exponentialRampToValueAtTime(45, time + duration * 0.9);

        const oscGain = this.ctx.createGain();
        oscGain.gain.setValueAtTime(0.5 * intensity, time);
        oscGain.gain.exponentialRampToValueAtTime(0.001, time + duration * 0.9);

        osc.connect(oscGain);
        oscGain.connect(dest);
        osc.start(time);
        osc.stop(time + duration);
        break;
      }

      case "dirt":
      case "sand":
      case "snow": {
        // Gritty lowpass filtered noise with crumbly decay
        const src = this.ctx.createBufferSource();
        src.buffer = this.pinkNoiseBuffer;
        src.playbackRate.value = pitchRate * (mat === "sand" ? 1.3 : 0.95);

        const filter = this.ctx.createBiquadFilter();
        filter.type = "lowpass";
        filter.frequency.setValueAtTime(mat === "sand" ? 1300 : 750, time);

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.001, time);
        gain.gain.linearRampToValueAtTime(0.75 * intensity, time + 0.004);
        gain.gain.exponentialRampToValueAtTime(0.001, time + duration * 0.95);

        src.connect(filter);
        filter.connect(gain);
        gain.connect(dest);
        src.start(time);
        src.stop(time + duration);
        break;
      }

      case "glass": {
        // High crystalline chime + sharp snap
        const freqs = [2400, 3200, 4100];
        freqs.forEach((f, idx) => {
          if (!this.ctx) return;
          const osc = this.ctx.createOscillator();
          osc.type = "sine";
          osc.frequency.setValueAtTime(f * pitchRate, time);

          const g = this.ctx.createGain();
          g.gain.setValueAtTime(0.2 * intensity, time + idx * 0.005);
          g.gain.exponentialRampToValueAtTime(0.001, time + duration * 1.4);

          osc.connect(g);
          g.connect(dest);
          osc.start(time);
          osc.stop(time + duration * 1.4);
        });

        // Noise click
        const src = this.ctx.createBufferSource();
        src.buffer = this.whiteNoiseBuffer;
        const hp = this.ctx.createBiquadFilter();
        hp.type = "highpass";
        hp.frequency.setValueAtTime(2500, time);
        const ng = this.ctx.createGain();
        ng.gain.setValueAtTime(0.4 * intensity, time);
        ng.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
        src.connect(hp);
        hp.connect(ng);
        ng.connect(dest);
        src.start(time);
        src.stop(time + 0.05);
        break;
      }

      case "wool": {
        // Muffled soft pat
        const src = this.ctx.createBufferSource();
        src.buffer = this.pinkNoiseBuffer;
        const lp = this.ctx.createBiquadFilter();
        lp.type = "lowpass";
        lp.frequency.setValueAtTime(380, time);
        const g = this.ctx.createGain();
        g.gain.setValueAtTime(0.001, time);
        g.gain.linearRampToValueAtTime(0.5 * intensity, time + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, time + duration * 0.7);
        src.connect(lp);
        lp.connect(g);
        g.connect(dest);
        src.start(time);
        src.stop(time + duration);
        break;
      }

      case "stone":
      default: {
        // High-frequency tink + resonant bandpass stone crunch
        const src = this.ctx.createBufferSource();
        src.buffer = this.whiteNoiseBuffer;
        src.playbackRate.value = pitchRate;

        const hp = this.ctx.createBiquadFilter();
        hp.type = "highpass";
        hp.frequency.setValueAtTime(1400 * pitchRate, time);

        const bp = this.ctx.createBiquadFilter();
        bp.type = "bandpass";
        bp.frequency.setValueAtTime(1800 * pitchRate, time);
        bp.Q.value = 3.0;

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.001, time);
        gain.gain.linearRampToValueAtTime(0.75 * intensity, time + 0.003);
        gain.gain.exponentialRampToValueAtTime(0.001, time + duration);

        src.connect(hp);
        hp.connect(bp);
        bp.connect(gain);
        gain.connect(dest);
        src.start(time);
        src.stop(time + duration);

        // Low stone punch
        const osc = this.ctx.createOscillator();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(190 * pitchRate, time);
        osc.frequency.exponentialRampToValueAtTime(55, time + 0.05);

        const og = this.ctx.createGain();
        og.gain.setValueAtTime(0.4 * intensity, time);
        og.gain.exponentialRampToValueAtTime(0.001, time + 0.05);

        osc.connect(og);
        og.connect(dest);
        osc.start(time);
        osc.stop(time + 0.06);
        break;
      }
    }
  }

  // =========================================================================
  // PLAYER ACTIONS: HURT ("OOF"), JUMP, FALL LANDING, EAT, POP, BOW
  // =========================================================================

  /**
   * Classic authentic Minecraft pitch-descending vocal "oof" damage sound.
   */
  playPlayerHurt(): void {
    const dest = this.getDestination(0.85);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "sawtooth";
    // Pitch sweeps from 240Hz down to 80Hz
    osc.frequency.setValueAtTime(235, t);
    osc.frequency.exponentialRampToValueAtTime(75, t + 0.22);

    // Formant lowpass filter gives the vocal "ooh" quality
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(580, t);
    filter.Q.value = 4.5;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.85, t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.24);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.25);
  }

  /**
   * Sound played when the player hits the ground after falling.
   */
  playFallLanding(fallDistance: number): void {
    const dest = this.getDestination(Math.min(1.0, 0.4 + fallDistance * 0.1));
    if (!dest || !this.ctx || !this.whiteNoiseBuffer) return;

    const t = this.ctx.currentTime;
    // Low heavy thud
    const osc = this.ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(95, t);
    osc.frequency.exponentialRampToValueAtTime(30, t + 0.18);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.7, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.2);

    // Ground crunch
    const src = this.ctx.createBufferSource();
    src.buffer = this.whiteNoiseBuffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(700, t);
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.5, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
    src.connect(filter);
    filter.connect(ng);
    ng.connect(dest);
    src.start(t);
    src.stop(t + 0.14);
  }

  /**
   * High-pitch upward cheerful bubble pop when picking up an item drop.
   */
  playItemPickup(): void {
    const dest = this.getDestination(0.45);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    // Pitch sweep upward: 880Hz to 1760Hz
    osc.frequency.setValueAtTime(880, t);
    osc.frequency.exponentialRampToValueAtTime(1760, t + 0.06);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.55, t + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.075);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.08);
  }

  /**
   * Sound played when eating food (crunching bite + gulp).
   */
  playEat(): void {
    const dest = this.getDestination(0.55);
    if (!dest || !this.ctx || !this.pinkNoiseBuffer) return;

    const t = this.ctx.currentTime;
    // 3 rapid munch crunches
    for (let i = 0; i < 3; i++) {
      const bt = t + i * 0.09;
      const src = this.ctx.createBufferSource();
      src.buffer = this.pinkNoiseBuffer;
      src.playbackRate.value = 1.1 + Math.random() * 0.3;

      const bp = this.ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.setValueAtTime(1200 + Math.random() * 400, bt);
      bp.Q.value = 2.5;

      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0.001, bt);
      g.gain.linearRampToValueAtTime(0.6, bt + 0.006);
      g.gain.exponentialRampToValueAtTime(0.001, bt + 0.065);

      src.connect(bp);
      bp.connect(g);
      g.connect(dest);
      src.start(bt);
      src.stop(bt + 0.07);
    }

    // Soft swallow gulp at end
    const gt = t + 0.32;
    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(160, gt);
    osc.frequency.exponentialRampToValueAtTime(260, gt + 0.07);
    osc.frequency.exponentialRampToValueAtTime(110, gt + 0.16);

    const gg = this.ctx.createGain();
    gg.gain.setValueAtTime(0.001, gt);
    gg.gain.linearRampToValueAtTime(0.35, gt + 0.02);
    gg.gain.exponentialRampToValueAtTime(0.001, gt + 0.18);

    osc.connect(gg);
    gg.connect(dest);
    osc.start(gt);
    osc.stop(gt + 0.2);
  }

  /**
   * Procedural burp sound played after finishing a meal (random.burp).
   */
  playBurp(): void {
    const dest = this.getDestination(0.5);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "sawtooth";
    const baseFreq = 115 + Math.random() * 20;
    osc.frequency.setValueAtTime(baseFreq, t);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.65, t + 0.32);

    const filter = this.ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(320, t);
    filter.frequency.linearRampToValueAtTime(180, t + 0.32);
    filter.Q.value = 3.5;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.45, t + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.32);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(dest);

    osc.start(t);
    osc.stop(t + 0.33);
  }

  /**
   * Bow string release twang when shooting an arrow.
   */
  playBowShoot(): void {
    const dest = this.getDestination(0.65);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    // String twang oscillator
    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(460, t);
    osc.frequency.exponentialRampToValueAtTime(150, t + 0.14);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.7, t + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.18);

    // String snap click
    if (this.whiteNoiseBuffer) {
      const src = this.ctx.createBufferSource();
      src.buffer = this.whiteNoiseBuffer;
      const bp = this.ctx.createBiquadFilter();
      bp.type = "highpass";
      bp.frequency.setValueAtTime(2200, t);
      const ng = this.ctx.createGain();
      ng.gain.setValueAtTime(0.5, t);
      ng.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
      src.connect(bp);
      bp.connect(ng);
      ng.connect(dest);
      src.start(t);
      src.stop(t + 0.05);
    }
  }

  /**
   * Woody thunk when an arrow hits a solid block.
   */
  playArrowHitBlock(): void {
    const dest = this.getDestination(0.5);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(210, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.09);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.65, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.11);
  }

  /**
   * Sound played when punching / attacking an entity.
   */
  playPunchHit(): void {
    const dest = this.getDestination(0.65);
    if (!dest || !this.ctx || !this.whiteNoiseBuffer) return;

    const t = this.ctx.currentTime;
    // Low punch impact
    const osc = this.ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(140, t);
    osc.frequency.exponentialRampToValueAtTime(40, t + 0.12);

    const og = this.ctx.createGain();
    og.gain.setValueAtTime(0.7, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

    osc.connect(og);
    og.connect(dest);
    osc.start(t);
    osc.stop(t + 0.14);

    // Slap noise
    const src = this.ctx.createBufferSource();
    src.buffer = this.whiteNoiseBuffer;
    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(900, t);
    bp.Q.value = 1.5;

    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.5, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.07);

    src.connect(bp);
    bp.connect(ng);
    ng.connect(dest);
    src.start(t);
    src.stop(t + 0.08);
  }

  /**
   * Splash sound when entering water or swimming.
   */
  playWaterSplash(isBig = true): void {
    const dest = this.getDestination(isBig ? 0.65 : 0.35);
    if (!dest || !this.ctx || !this.pinkNoiseBuffer) return;

    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.pinkNoiseBuffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = "bandpass";
    filter.frequency.setValueAtTime(1800, t);
    filter.frequency.exponentialRampToValueAtTime(320, t + (isBig ? 0.35 : 0.2));
    filter.Q.value = 2.0;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.8, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + (isBig ? 0.38 : 0.22));

    src.connect(filter);
    filter.connect(gain);
    gain.connect(dest);
    src.start(t);
    src.stop(t + 0.4);

    // Liquid bubble chirp
    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(340, t);
    osc.frequency.exponentialRampToValueAtTime(680, t + 0.08);

    const og = this.ctx.createGain();
    og.gain.setValueAtTime(0.3, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.09);

    osc.connect(og);
    og.connect(dest);
    osc.start(t);
    osc.stop(t + 0.1);
  }

  /**
   * Tool durability exhausted break sound.
   */
  playToolBreak(): void {
    const dest = this.getDestination(0.7);
    if (!dest || !this.ctx || !this.whiteNoiseBuffer) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(1200, t);
    osc.frequency.exponentialRampToValueAtTime(180, t + 0.15);

    const og = this.ctx.createGain();
    og.gain.setValueAtTime(0.5, t);
    og.gain.exponentialRampToValueAtTime(0.001, t + 0.16);

    osc.connect(og);
    og.connect(dest);
    osc.start(t);
    osc.stop(t + 0.18);

    // High snap noise
    const src = this.ctx.createBufferSource();
    src.buffer = this.whiteNoiseBuffer;
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.setValueAtTime(2400, t);
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.6, t);
    ng.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    src.connect(hp);
    hp.connect(ng);
    ng.connect(dest);
    src.start(t);
    src.stop(t + 0.12);
  }

  /**
   * Zombie guttural groan sound.
   */
  playZombieGroan(pan = 0, distanceFactor = 1.0): void {
    const dest = this.getDestination(0.45 * distanceFactor, pan);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "sawtooth";
    const baseFreq = 95 + Math.random() * 20;
    osc.frequency.setValueAtTime(baseFreq, t);
    osc.frequency.linearRampToValueAtTime(baseFreq + 12, t + 0.4);
    osc.frequency.linearRampToValueAtTime(baseFreq - 15, t + 0.9);

    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(460, t);
    bp.frequency.linearRampToValueAtTime(380, t + 0.9);
    bp.Q.value = 4.0;

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.linearRampToValueAtTime(0.65, t + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.95);

    osc.connect(bp);
    bp.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 1.0);
  }

  /**
   * Skeleton dry bone rattle sound.
   */
  playSkeletonRattle(pan = 0, distanceFactor = 1.0): void {
    const dest = this.getDestination(0.45 * distanceFactor, pan);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const bt = t + i * 0.045;
      const osc = this.ctx.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(700 + Math.random() * 300, bt);
      osc.frequency.exponentialRampToValueAtTime(200, bt + 0.04);

      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.5, bt);
      gain.gain.exponentialRampToValueAtTime(0.001, bt + 0.045);

      osc.connect(gain);
      gain.connect(dest);
      osc.start(bt);
      osc.stop(bt + 0.05);
    }
  }

  /**
   * Spider chittering hiss / screech sound ("mob.spider.say").
   */
  playSpiderHiss(pan = 0, distanceFactor = 1.0): void {
    const dest = this.getDestination(0.5 * distanceFactor, pan);
    if (!dest || !this.ctx || !this.whiteNoiseBuffer) return;

    const t = this.ctx.currentTime;

    // Resonant bandpass noise for the insectoid hiss
    const noise = this.ctx.createBufferSource();
    noise.buffer = this.whiteNoiseBuffer;

    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(1400, t);
    bp.frequency.exponentialRampToValueAtTime(950, t + 0.45);
    bp.Q.setValueAtTime(5.5, t);

    // Raspy amplitude flutter at 35Hz
    const lfo = this.ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.setValueAtTime(35, t);

    const lfoGain = this.ctx.createGain();
    lfoGain.gain.setValueAtTime(0.35, t);

    const mainGain = this.ctx.createGain();
    mainGain.gain.setValueAtTime(0.01, t);
    mainGain.gain.linearRampToValueAtTime(0.7, t + 0.08);
    mainGain.gain.exponentialRampToValueAtTime(0.001, t + 0.45);

    lfo.connect(lfoGain.gain);
    noise.connect(bp);
    bp.connect(mainGain);
    mainGain.connect(dest);

    lfo.start(t);
    lfo.stop(t + 0.46);
    noise.start(t);
    noise.stop(t + 0.46);
  }

  /**
   * Spider dying screech sound ("mob.spider.death").
   */
  playSpiderDeath(pan = 0, distanceFactor = 1.0): void {
    const dest = this.getDestination(0.55 * distanceFactor, pan);
    if (!dest || !this.ctx || !this.whiteNoiseBuffer) return;

    const t = this.ctx.currentTime;

    const noise = this.ctx.createBufferSource();
    noise.buffer = this.whiteNoiseBuffer;

    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.setValueAtTime(1200, t);
    bp.frequency.exponentialRampToValueAtTime(320, t + 0.7);
    bp.Q.setValueAtTime(4.0, t);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.01, t);
    gain.gain.linearRampToValueAtTime(0.75, t + 0.09);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.7);

    noise.connect(bp);
    bp.connect(gain);
    gain.connect(dest);

    noise.start(t);
    noise.stop(t + 0.72);
  }

  /**
   * Subtle UI click for inventory clicks, slot swapping, buttons.
   */
  playUiClick(): void {
    const dest = this.getDestination(0.25);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(1150, t);
    osc.frequency.exponentialRampToValueAtTime(450, t + 0.02);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.025);

    osc.connect(gain);
    gain.connect(dest);
    osc.start(t);
    osc.stop(t + 0.03);
  }

  /**
   * Creeper hissing fuse sound before detonation ("sssss...").
   */
  playCreeperFuse(pan = 0, distanceFactor = 1.0): void {
    const dest = this.getDestination(0.65 * distanceFactor, pan);
    if (!dest || !this.ctx || !this.whiteNoiseBuffer) return;

    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.whiteNoiseBuffer;

    const bp = this.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.Q.setValueAtTime(3.0, t);
    bp.frequency.setValueAtTime(2200, t);
    bp.frequency.exponentialRampToValueAtTime(3400, t + 1.4);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.1, t);
    gain.gain.linearRampToValueAtTime(0.5, t + 1.2);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 1.5);

    src.connect(bp);
    bp.connect(gain);
    gain.connect(dest);
    src.start(t);
    src.stop(t + 1.5);
  }

  /**
   * Deep explosive detonation blast and sub-bass rumble.
   */
  playExplosion(pan = 0, distanceFactor = 1.0): void {
    const dest = this.getDestination(1.0 * distanceFactor, pan);
    if (!dest || !this.ctx) return;

    const t = this.ctx.currentTime;

    // 1. Deep sub-bass impact thump
    const sub = this.ctx.createOscillator();
    sub.type = "triangle";
    sub.frequency.setValueAtTime(140, t);
    sub.frequency.exponentialRampToValueAtTime(28, t + 0.6);

    const subGain = this.ctx.createGain();
    subGain.gain.setValueAtTime(0.9, t);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.9);

    sub.connect(subGain);
    subGain.connect(dest);
    sub.start(t);
    sub.stop(t + 0.95);

    // 2. Heavy blast noise burst and debris rumble
    if (this.pinkNoiseBuffer) {
      const noise = this.ctx.createBufferSource();
      noise.buffer = this.pinkNoiseBuffer;

      const lp = this.ctx.createBiquadFilter();
      lp.type = "lowpass";
      lp.frequency.setValueAtTime(1200, t);
      lp.frequency.exponentialRampToValueAtTime(90, t + 1.2);

      const noiseGain = this.ctx.createGain();
      noiseGain.gain.setValueAtTime(0.95, t);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 1.3);

      noise.connect(lp);
      lp.connect(noiseGain);
      noiseGain.connect(dest);
      noise.start(t);
      noise.stop(t + 1.35);
    }

    // 3. Sharp initial crackle snap
    if (this.whiteNoiseBuffer) {
      const snap = this.ctx.createBufferSource();
      snap.buffer = this.whiteNoiseBuffer;

      const hp = this.ctx.createBiquadFilter();
      hp.type = "highpass";
      hp.frequency.setValueAtTime(1800, t);

      const snapGain = this.ctx.createGain();
      snapGain.gain.setValueAtTime(0.7, t);
      snapGain.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

      snap.connect(hp);
      hp.connect(snapGain);
      snapGain.connect(dest);
      snap.start(t);
      snap.stop(t + 0.15);
    }
  }

  /**
   * Primed TNT fuse ignition sizzle sound.
   */
  playTntPrime(pan = 0, distanceFactor = 1.0): void {
    const dest = this.getDestination(0.55 * distanceFactor, pan);
    if (!dest || !this.ctx || !this.whiteNoiseBuffer) return;

    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource();
    src.buffer = this.whiteNoiseBuffer;

    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.setValueAtTime(3000, t);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0.4, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

    src.connect(hp);
    hp.connect(gain);
    gain.connect(dest);
    src.start(t);
    src.stop(t + 0.35);
  }
}

/** Global singleton instance for easy access across the game engine and UI. */
export const sound = new SoundEngine();
